import { suggestedTransportTypeOrEmpty } from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import {
  Commodity,
  Load,
  NormalizedLoad,
  Unit,
  createInitLoad,
  createInitRateData,
  createInitSpecs,
  createNewCommodity,
  normalizeLoad,
} from 'types/Load';
import { Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { SuggestedLoad } from 'types/suggestions/LoadBuildingSuggestions';
import { normalizeToArray } from 'utils/loadInfoAndBuilding';

import {
  MGB_DEFAULT_COLLECTION_METHOD,
  MGB_DEFAULT_PIECES_UNIT,
  MGB_DEFAULT_RATE_MODE,
  MGB_DEFAULT_WEIGHT_UNIT,
  MGB_PIECE_UNIT_OPTIONS,
  freightClassList,
  pieceUnitsList,
} from '../constants';
import {
  buildMGBStopsFromPickupConsignee,
  ensureMGBRouteStops,
} from '../mgbStops';
import { MGBCollectionMethod, MGBLoadNoteType } from '../types';
import {
  applyQuoteNumberSelectionToRefs,
  formatDrumkitQuoteNumber,
  normalizeMGBAdditionalReferencesFromSuggestion,
  stripEmptySystemManagedRefs,
} from './references';

/** Wrapper-level LB quote association (not on `suggested` payload). */
export type MGBSuggestionQuoteAssociation = {
  drumkitQuoteNumber?: string;
  quoteRequestId?: number;
  lowBuyRate?: number;
  highBuyRate?: number;
};

/** Map legacy/API weight units to MG-Boomi dropdown values (`lb` or `kg` only). */
export function normalizeMGBWeightUnit(
  unit: string | null | undefined
): string {
  const normalized = unit?.trim().toLowerCase() ?? '';
  if (
    normalized === 'lb' ||
    normalized === 'lbs' ||
    normalized === 'pound' ||
    normalized === 'pounds'
  ) {
    return MGB_DEFAULT_WEIGHT_UNIT;
  }

  if (
    normalized === 'kg' ||
    normalized === 'kilogram' ||
    normalized === 'kilograms'
  ) {
    return 'kg';
  }

  return MGB_DEFAULT_WEIGHT_UNIT;
}

const MGB_WEIGHT_UNIT_TO_UNIT_ENUM: Record<string, Unit> = {
  [MGB_DEFAULT_WEIGHT_UNIT]: Unit.Pound,
  kg: Unit.Kg,
};

/** Map form/API weight unit strings to `ValueUnit.unit` enum values. */
export function toMGBGrossWeightUnit(
  unit: Unit | string | null | undefined
): Unit {
  const normalized = normalizeMGBWeightUnit(unit == null ? '' : String(unit));
  return MGB_WEIGHT_UNIT_TO_UNIT_ENUM[normalized] ?? Unit.Pound;
}

const MGB_PIECE_ALIAS_TO_CODE: Record<string, string> = {
  each: 'EA',
  ea: 'EA',
  piece: 'PCS',
  pieces: 'PCS',
  pcs: 'PCS',
  cases: 'CAS',
  case: 'CAS',
  cas: 'CAS',
  pallets: 'PALLET',
  pallet: 'PALLET',
  plt: 'PALLET',
  plts: 'PALLET',
  units: 'EA',
  unit: 'EA',
  bag: 'BAG',
  bags: 'BAG',
  box: 'BOX',
  boxes: 'BOX',
  bundle: 'BDL',
  bundles: 'BDL',
  bushel: 'BUS',
  bushels: 'BUS',
  carton: 'CTN',
  cartons: 'CTN',
  crate: 'CRT',
  crates: 'CRT',
  drum: 'DRM',
  drums: 'DRM',
  package: 'PKG',
  packages: 'PKG',
  pail: 'PAL',
  pails: 'PAL',
  gallon: 'GAL',
  gallons: 'GAL',
  roll: 'ROL',
  rolls: 'ROL',
  tote: 'TOT',
  totes: 'TOT',
};

const MGB_PIECE_CODE_SET = new Set(
  pieceUnitsList.map((code) => code.toUpperCase())
);

export function normalizeMGBPiecesUnit(
  unit: Unit | string | null | undefined
): string {
  const raw = String(unit ?? '').trim();
  if (!raw) {
    return MGB_DEFAULT_PIECES_UNIT;
  }

  const exact = pieceUnitsList.find(
    (option) => option.toLowerCase() === raw.toLowerCase()
  );
  if (exact) {
    return exact;
  }

  const aliased = MGB_PIECE_ALIAS_TO_CODE[raw.toLowerCase()];
  if (aliased && MGB_PIECE_CODE_SET.has(aliased.toUpperCase())) {
    return aliased;
  }

  return MGB_DEFAULT_PIECES_UNIT;
}

export function toMGBPiecesUnit(unit: Unit | string | null | undefined): Unit {
  return normalizeMGBPiecesUnit(unit) as Unit;
}

export function toMGBPiecesUnitOption(
  unit: Unit | string | null | undefined
): string {
  const raw = String(unit ?? '').trim();
  if (!raw) {
    return '';
  }

  const exact = pieceUnitsList.find(
    (option) => option.toLowerCase() === raw.toLowerCase()
  );

  if (exact) {
    return exact;
  }

  return MGB_PIECE_ALIAS_TO_CODE[raw.toLowerCase()] ?? raw;
}

export function mgPieceUnitLabel(unit: string | null | undefined): string {
  const code = String(unit ?? '').trim();
  if (!code) {
    return '';
  }

  const match = MGB_PIECE_UNIT_OPTIONS.find(
    (option) => option.value.toLowerCase() === code.toLowerCase()
  );

  return match?.label ?? code;
}

/**
 * Normalize an API freight class (e.g. `65.0`) to the matching dropdown option
 * (e.g. `65`) by numeric equality, preserving fractional classes like `77.5`.
 */
export function normalizeMGBFreightClass(
  value: string | null | undefined
): string {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '';
  }

  const num = Number(raw);
  if (!Number.isNaN(num)) {
    const match = freightClassList.find((option) => Number(option) === num);
    if (match) {
      return match;
    }
  }

  return raw;
}

export function createMGBCommodity(options?: {
  pickupStopNumber?: number;
  dropStopNumber?: number;
}): Commodity {
  return {
    ...createNewCommodity(),
    quantity: undefined as unknown as number,
    // Use dropdown option code `EA` (not Unit.Each / "Each") so the select shows a value.
    totalPieces: {
      val: undefined as unknown as number,
      unit: MGB_DEFAULT_PIECES_UNIT as Unit,
    },
    weightTotal: undefined as unknown as number,
    length: undefined as unknown as number,
    width: undefined as unknown as number,
    height: undefined as unknown as number,
    weightUnit: MGB_DEFAULT_WEIGHT_UNIT,
    dimensionUnit: 'in',
    grossWeight: { val: undefined as unknown as number, unit: Unit.Pound },
    ...(options?.pickupStopNumber != null
      ? { pickupStopNumber: options.pickupStopNumber }
      : {}),
    ...(options?.dropStopNumber != null
      ? { dropStopNumber: options.dropStopNumber }
      : {}),
  };
}

/** Placeholder internal note so create UI opens with one editable row (not required). */
export function createMGBNote(): NormalizedLoad['notes'][number] {
  return {
    note: '',
    noteType: MGBLoadNoteType.InternalNote,
    updatedBy: '',
    createdAt: '',
    isException: null,
    isOnTime: null,
    source: '',
  };
}

/** True when a commodity row has no meaningful user-entered data (placeholder). */
export function isEmptyMGBCommodity(c: Undef<Commodity>): boolean {
  if (!c) {
    return true;
  }

  const hasPieces = Number(c.totalPieces?.val) > 0 || Number(c.quantity) > 0;

  return (
    !c.description?.trim() &&
    !hasPieces &&
    !(commodityEffectiveWeight(c) > 0) &&
    !c.referenceNumber?.trim()
  );
}

export function isPositiveMGBDimension(value: unknown): boolean {
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

/** Non-empty commodity assigned to this route stop index (for delete guards). */
export function stopHasAssignedMGBFreight(
  commodities: Undef<Commodity[]>,
  index: number
): boolean {
  return (commodities ?? []).some(
    (c) =>
      c &&
      !isEmptyMGBCommodity(c) &&
      (c.pickupStopNumber === index || c.dropStopNumber === index)
  );
}

function optionalPositiveFormNumber(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Line weight for summaries and empty-row checks: prefer weightTotal, else grossWeight.val. */
export function commodityEffectiveWeight(c: Commodity): number {
  const weightTotal = Number(c.weightTotal);
  if (Number.isFinite(weightTotal) && weightTotal > 0) {
    return weightTotal;
  }

  const gross = Number(c.grossWeight?.val);

  return Number.isFinite(gross) && gross > 0 ? gross : 0;
}

export function commodityWeightUnit(c: Commodity): string {
  const explicit = c.weightUnit?.trim() ?? '';
  if (explicit) {
    return normalizeMGBWeightUnit(explicit);
  }
  return normalizeMGBWeightUnit(
    c.grossWeight?.unit != null ? String(c.grossWeight.unit) : ''
  );
}

function resolveMGBCommodityWeightUnit(c: Commodity): string {
  const weightTotal = Number(c.weightTotal);
  if (Number.isFinite(weightTotal) && weightTotal > 0) {
    return commodityWeightUnit(c);
  }

  const grossUnit =
    c.grossWeight?.unit != null ? String(c.grossWeight.unit).trim() : '';
  if (grossUnit) {
    return normalizeMGBWeightUnit(grossUnit);
  }

  return commodityWeightUnit(c);
}

// Same factor as Revenova load-summary conversion (kg → lbs).
const KG_TO_LBS = 2.20462;

function convertMGBWeightToLbs(weight: number, unit: string): number {
  if (!(weight > 0)) {
    return 0;
  }
  if (normalizeMGBWeightUnit(unit) === 'kg') {
    return weight * KG_TO_LBS;
  }
  return weight;
}

function roundMGBWeightLbs(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Convert one commodity's effective weight to lbs for the load-summary total. */
export function commodityEffectiveWeightLbs(c: Commodity): number {
  const weight = commodityEffectiveWeight(c);
  if (!(weight > 0)) {
    return 0;
  }
  return convertMGBWeightToLbs(weight, resolveMGBCommodityWeightUnit(c));
}

/**
 * Sum commodity weights in lbs. MG ImportWeb uses per-item UOMs; this total is
 * for the Load Summary UI and Drumkit `specifications.totalWeight` (unit lbs).
 */
export function sumMGBCommodityWeightLbs(
  commodities: Undef<Commodity[]>
): number {
  const total = (commodities ?? []).reduce(
    (sum, item) => sum + commodityEffectiveWeightLbs(item),
    0
  );

  return roundMGBWeightLbs(total);
}

/** Whether an MGB load is already cancelled (status or freight tracking ID). */
export function isMGBLoadCancelled(
  load: Pick<Load, 'status' | 'freightTrackingID'>
): boolean {
  const status = load.status?.trim().toLowerCase() ?? '';
  if (status) {
    if (status === 'cancelled' || status === 'canceled') {
      return true;
    }
    const tokens = status.split(/[\s\-_/]+/);
    if (tokens.some((t) => t === 'cancelled' || t === 'canceled')) {
      return true;
    }
  }

  const freightId = load.freightTrackingID ?? '';

  return /-Cancelled-|-Canceled-/i.test(freightId);
}

function withMGBCommodityDefaults(
  commodities: Commodity[] | undefined
): Commodity[] {
  return (commodities ?? []).map((c) => {
    const weightUnit = normalizeMGBWeightUnit(
      c.weightUnit?.trim() ||
        (c.grossWeight?.unit != null ? String(c.grossWeight.unit) : '')
    );

    return {
      ...c,
      quantity: optionalPositiveFormNumber(c.quantity) as unknown as number,
      weightTotal: optionalPositiveFormNumber(
        c.weightTotal
      ) as unknown as number,
      length: optionalPositiveFormNumber(c.length) as unknown as number,
      width: optionalPositiveFormNumber(c.width) as unknown as number,
      height: optionalPositiveFormNumber(c.height) as unknown as number,
      dimensionUnit: c.dimensionUnit?.trim?.() || 'in',
      weightUnit,
      freightClass: normalizeMGBFreightClass(c.freightClass),
      totalPieces: c.totalPieces
        ? {
            ...c.totalPieces,
            val: optionalPositiveFormNumber(
              c.totalPieces.val
            ) as unknown as number,
            unit: (toMGBPiecesUnitOption(c.totalPieces.unit) ||
              c.totalPieces.unit) as Unit,
          }
        : c.totalPieces,
      grossWeight: c.grossWeight
        ? {
            ...c.grossWeight,
            val: optionalPositiveFormNumber(
              c.grossWeight.val
            ) as unknown as number,
            unit: toMGBGrossWeightUnit(c.grossWeight.unit || weightUnit),
          }
        : c.grossWeight,
    };
  });
}

/** Normalize note type from API or legacy display strings to form/API enum value. */
export function toFormNoteType(value: string): string {
  const trimmed = value.trim();
  switch (trimmed) {
    case 'Internal Note':
    case MGBLoadNoteType.InternalNote:
      return MGBLoadNoteType.InternalNote;
    case 'Special Instructions':
    case 'SpecialInstruction':
    case MGBLoadNoteType.SpecialInstructions:
      return MGBLoadNoteType.SpecialInstructions;
    default:
      return trimmed;
  }
}

/** Map form note type to API wire value (same as form storage). */
export function toApiNoteType(value: string): string {
  return toFormNoteType(value);
}

export function toFormCollectionMethod(value: string): MGBCollectionMethod {
  const normalized = value.toLowerCase().replace(/[\s-]/g, '');
  if (normalized === 'prepaid') {
    return MGBCollectionMethod.Prepaid;
  }

  if (normalized === 'collect') {
    return MGBCollectionMethod.Collect;
  }

  if (normalized === 'thirdparty') {
    return MGBCollectionMethod.ThirdParty;
  }

  return MGBCollectionMethod.Unset;
}

export function toApiCollectionMethod(value: string): string {
  switch (value as MGBCollectionMethod) {
    case MGBCollectionMethod.Prepaid:
      return 'prepaid';
    case MGBCollectionMethod.Collect:
      return 'collect';
    case MGBCollectionMethod.ThirdParty:
      return 'third-party';
    default:
      return value;
  }
}

export function initMGBLoadForm(
  tmsName: string = TMS.MercurygateBoomi
): NormalizedLoad {
  const pickup = {
    ...createInitLoad().pickup,
    country: 'USA',
  };
  const consignee = {
    ...createInitLoad().consignee,
    country: 'USA',
  };
  const stops = buildMGBStopsFromPickupConsignee(pickup, consignee);

  return {
    ...normalizeLoad(tmsName, {
      ...createInitLoad(),
      mode: 'TL',
      specifications: {
        ...createInitSpecs(),
        transportType: '',
        transportSize: '',
        planningComment: '',
      },
      rateData: {
        ...createInitRateData(),
        collectionMethod: MGB_DEFAULT_COLLECTION_METHOD,
        customerRateType: MGB_DEFAULT_RATE_MODE,
        customerLineHaulCharge: { val: 0, unit: Unit.USD },
      },
      pickup,
      consignee,
      commodities: [
        {
          ...createMGBCommodity(),
          pickupStopNumber: 0,
          dropStopNumber: 1,
        },
      ],
      notes: [createMGBNote()],
      stops,
    }),
    accessorials: [],
    stops,
    moreThanTwoStops: false,
  };
}

/** Merge AI load-building suggestion fields into MG-Boomi form defaults. */
export function buildMGBFormFromSuggestion(
  suggested: SuggestedLoad,
  tmsName: string = TMS.MercurygateBoomi,
  quoteAssociation?: MGBSuggestionQuoteAssociation
): NormalizedLoad {
  const base = initMGBLoadForm(tmsName);
  const commodities = normalizeToArray(suggested.commodities) ?? [];
  const notes = normalizeToArray(suggested.notes) ?? [];
  const additionalReferences = normalizeMGBAdditionalReferencesFromSuggestion(
    normalizeToArray(suggested.additionalReferences)
  );

  const merged = withMGBFormDefaults(
    normalizeLoad(tmsName, {
      ...createInitLoad(),
      ...suggested,
      mode: suggested.mode?.trim() || 'TL',
      customer: {
        ...base.customer,
        ...suggested.customer,
      },
      pickup: {
        ...base.pickup,
        ...suggested.pickup,
        country: suggested.pickup?.country?.trim() || 'USA',
      },
      consignee: {
        ...base.consignee,
        ...suggested.consignee,
        country: suggested.consignee?.country?.trim() || 'USA',
      },
      commodities: commodities.length > 0 ? commodities : base.commodities,
      notes: notes.length > 0 ? notes : base.notes,
      additionalReferences:
        additionalReferences.length > 0
          ? additionalReferences
          : base.additionalReferences,
      rateData: {
        ...base.rateData,
        ...suggested.rateData,
      },
      specifications: {
        ...base.specifications,
        ...suggested.specifications,
        transportType: suggestedTransportTypeOrEmpty(suggested.specifications),
      },
    } as unknown as Load)
  );

  // DKQN lives on the LB suggestion wrapper, not `suggested`. Bake it into
  // create defaultValues so parent reset cannot wipe the association.
  const dkqn =
    quoteAssociation?.drumkitQuoteNumber?.trim() ||
    (quoteAssociation?.quoteRequestId && quoteAssociation.quoteRequestId > 0
      ? formatDrumkitQuoteNumber(quoteAssociation.quoteRequestId)
      : '');
  if (!dkqn) {
    return merged;
  }

  return {
    ...merged,
    additionalReferences: applyQuoteNumberSelectionToRefs(
      merged.additionalReferences,
      {
        drumkitQuoteNumber: dkqn,
        lowBuyRate: quoteAssociation?.lowBuyRate,
        highBuyRate: quoteAssociation?.highBuyRate,
        syncRateBand: true,
      }
    ),
  };
}

/** Map API collection method to UI select values after `normalizeLoad`. */
export function withMGBFormDefaults(
  normalized: NormalizedLoad
): NormalizedLoad {
  const collectionMethod = toFormCollectionMethod(
    normalized.rateData?.collectionMethod ?? ''
  );
  const stops = ensureMGBRouteStops(normalized);

  return {
    ...normalized,
    stops,
    moreThanTwoStops: stops.length > 2,
    rateData: {
      ...normalized.rateData,
      collectionMethod:
        collectionMethod === MGBCollectionMethod.Unset
          ? MGB_DEFAULT_COLLECTION_METHOD
          : collectionMethod,
      customerRateType:
        normalized.rateData?.customerRateType?.trim() || MGB_DEFAULT_RATE_MODE,
    },
    notes: (normalized.notes ?? [])
      .map((note) => ({
        ...note,
        noteType: toFormNoteType(note.noteType ?? ''),
      }))
      // Special Instructions live in Load Summary (specifications.planningComment),
      // so the Notes section only shows internal notes.
      .filter((note) => note.noteType !== MGBLoadNoteType.SpecialInstructions),
    commodities: withMGBCommodityDefaults(normalized.commodities),
    additionalReferences: stripEmptySystemManagedRefs(
      normalized.additionalReferences
    ),
  };
}
